import * as React from "react"
import { gql, useApolloClient } from "@apollo/client"
import { Button, SimpleGrid, Stack, useDisclosure } from "@chakra-ui/react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { useRouter } from "next/router"

import { ACCESS_TOKEN, REFRESH_TOKEN_KEY } from "lib/config"
import {
  MeDocument,
  ShortRegisterInput,
  useGetUpcomingFinalDateQuery,
  useShortRegisterMutation,
} from "lib/graphql"
import { useForm } from "lib/hooks/useForm"
import yup from "lib/yup"
import { Form } from "components/Form"
import { FormError } from "components/FormError"
import { Input } from "components/Input"
import { Modal } from "components/Modal"
import { RegisterExtraInfoForm } from "components/RegisterExtraInfoForm"

import { DateInput } from "./DateInput"
import { RegisterAgeCheckMessage } from "./RegisterAgeCheckMessage"
import { RegisterExtraInfo } from "./RegisterExtraInfoForm"
import { Select } from "./Select"
import { regions } from "lib/static/filters"

dayjs.extend(utc)

const _ = gql`
  mutation ShortRegister($data: ShortRegisterInput!) {
    shortRegister(data: $data) {
      user {
        ...Me
      }
      token
      refreshToken
    }
  }
`

const ShortRegisterSchema = yup.object().shape({
  email: yup.string().email("Ongeldig e-mailadres").required("Verplicht"),
  password: yup.string().min(8, "Moet ten minste 8 karakaters bevatten").required("Verplicht"),
  firstName: yup.string().required("Verplicht"),
  dateOfBirth: yup.string().required("Verplicht"),
  telephone: yup.string().required("Verplicht"),
  region: yup.string().required("Verplicht"),
})

interface Props {
  redirect?: string
}

export function ShortRegisterForm({ redirect }: Props) {
  const client = useApolloClient()
  const router = useRouter()

  const [shortRegister, { loading }] = useShortRegisterMutation()
  const extraInfoModalProps = useDisclosure()
  const ageCheckModalProps = useDisclosure()

  const { data } = useGetUpcomingFinalDateQuery()
  const finalDate = dayjs(data?.upcomingFinalDate)

  const defaultValues = {
    email: "",
    password: "",
    firstName: "",
    telephone: "",
    dateOfBirth: "",
    region: "",
    isOver16: false,
    parentEmail: "",
    isPrivacyCheckAccepted: false,
    isParentNotified: false,
  }

  const form = useForm({ schema: ShortRegisterSchema, defaultValues })

  const handleRegister = (values: RegisterExtraInfo) => {
    const userFormValues = form.getValues()
    const data: ShortRegisterInput = {
      email: userFormValues.email,
      password: userFormValues.password,
      firstName: userFormValues.firstName,
      telephone: userFormValues.telephone,
      dateOfBirth: userFormValues.dateOfBirth,
      region: userFormValues.region,
      isOver16: values.isOver16,
      parentEmail: values.isParentNotifiedDisabled ? null : values.parentEmail,
      isPrivacyCheckAccepted: values.isPrivacyCheckAccepted,
      isParentNotified: !values.isParentNotifiedDisabled,
    }

    return form.handler(() => shortRegister({ variables: { data } }), {
      onSuccess: async (res) => {
        extraInfoModalProps.onClose()
        localStorage.setItem(ACCESS_TOKEN, res.shortRegister.token)
        await fetch("/api/login", {
          method: "post",
          body: JSON.stringify({
            [REFRESH_TOKEN_KEY]: res.shortRegister.refreshToken,
          }),
        })
        client.writeQuery({ query: MeDocument, data: { me: res.shortRegister.user } })
        router.replace(redirect || `/users/${res.shortRegister.user.id}`)
      },
    })
  }

  const handleAgeCheck = () => {
    if (dayjs.utc(form.getValues().dateOfBirth).add(20, "year").isBefore(finalDate)) {
      ageCheckModalProps.onOpen()
    } else {
      extraInfoModalProps.onOpen()
    }
  }
  return (
    <>
      <Form onSubmit={handleAgeCheck} {...form} id="register-form">
        <Stack spacing={3} p={2}>
          <Input name="email" label="Emailadres" placeholder="ryan@email.com" />
          <Input name="password" label="Wachtwoord" type="password" placeholder="********" />
          <Input name="firstName" label="Voornaam" placeholder="Ryan" />
          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={3}>
            <DateInput name="dateOfBirth" label="Geboortedatum" />
            <Input name="telephone" label="Telefoon" placeholder="06-12345678" />
          </SimpleGrid>
          <Select name="region" label="Regio" options={regions} placeholder="Kies" />
          <Button
            variant="outline"
            colorScheme="yellow"
            type="submit"
            w="100%"
            isLoading={loading}
            isDisabled={loading}
            mt={4}
          >
            Meld je aan
          </Button>
          <FormError />
        </Stack>
      </Form>
      <Modal {...ageCheckModalProps}>
        <RegisterAgeCheckMessage
          openExtraInfoModal={extraInfoModalProps.onOpen}
          onClose={ageCheckModalProps.onClose}
        />
      </Modal>
      <Modal {...extraInfoModalProps} size="2xl">
        <RegisterExtraInfoForm handleRegister={handleRegister} />
      </Modal>
    </>
  )
}
